import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { USDollar } from "../../../utils/func/currency";

export type DetailLine = {
  // normalized by backend
  date: string; // ISO date
  transactionType: "Invoice" | "Payment";
  memo: string;
  num?: string;
  paymentMethod?: string;
  trackingNumber?: string;
  checkNumber?: string;
  amount: number;
};

export type InvoiceRow = {
  Id: string;
  DocNumber: string;
  TxnDate: string;
  DueDate?: string;
  invoiceAmount: number;
  totalApplied: number;
  remainingBalance: number;
  TransactionType: "Invoice";
  CustomerRef: { value: string; name: string };
  details: DetailLine[];
};

export type CustomerGroup = {
  _id: { value: string; name: string };
  CustomerRef: { value: string; name: string };
  idClient: string;
  invoices: InvoiceRow[];
  total: {
    invoiced: number;
    applied: number;
    remaining: number;
  };
};

// ---------- OUTER TABLE (customer totals) ----------
export const getInvPaymentsColumns = ({
  onInvoiceCellClick,
}: {
  onInvoiceCellClick: (invoice: InvoiceRow) => void;
}): ColumnsType<CustomerGroup> => [
  {
    key: "customer",
    title: "Customer",
    fixed: "left",
    width: 260,
    render: (_, record) => record?._id?.name || "-",
    sorter: (a, b) => (a?._id?.name || "").localeCompare(b?._id?.name || ""),
  },
  {
    key: "customer2",
    title: "CustomerRef",
    fixed: "left",
    width: 260,
    render: (_, record) => record?.CustomerRef?.name || "-",
    sorter: (a, b) => (a?._id?.name || "").localeCompare(b?._id?.name || ""),
  },
  {
    key: "invoicesCount",
    title: "# Invoices",
    width: 110,
    align: "right" as const,
    render: (_, record) => record?.invoices?.length ?? 0,
    sorter: (a, b) => (a?.invoices?.length ?? 0) - (b?.invoices?.length ?? 0),
  },
  {
    key: "invoiced",
    title: "Invoiced",
    width: 150,
    align: "right" as const,
    render: (_, r) => USDollar.format(r?.total?.invoiced || 0),
    sorter: (a, b) => (a?.total?.invoiced || 0) - (b?.total?.invoiced || 0),
  },
  {
    key: "applied",
    title: "Applied",
    width: 150,
    align: "right" as const,
    render: (_, r) => USDollar.format(r?.total?.applied || 0),
    sorter: (a, b) => (a?.total?.applied || 0) - (b?.total?.applied || 0),
  },
  {
    key: "remaining",
    title: "Remaining",
    width: 150,
    align: "right" as const,
    render: (_, r) => USDollar.format(Math.max(0, r?.total?.remaining || 0)),
    sorter: (a, b) => (a?.total?.remaining || 0) - (b?.total?.remaining || 0),
  },
];

// ---------- INNER TABLE (expanded details) ----------
export type DetailRow = {
  key: string;
  invoiceId: string;
  invoiceNumber: string;
  date: string;
  transactionType: "Invoice" | "Payment";
  memo: string;
  num?: string;
  paymentMethod?: string;
  trackingNumber?: string;
  checkNumber?: string;
  amount: number;
  // pass-through of the invoice for drawer open
  _invoice?: InvoiceRow;
};

export const getDetailColumns = ({
  onOpenInvoice,
}: {
  onOpenInvoice: (invoice: InvoiceRow) => void;
}): ColumnsType<DetailRow> => [
  // {
  //   key: "invoice",
  //   title: "Invoice #",
  //   dataIndex: "invoiceNumber",
  //   width: 140,
  //   // render: (val, row) => (
  //   //   <a onClick={() => row._invoice && onOpenInvoice(row._invoice)}>
  //   //     {val || "-"}
  //   //   </a>
  //   // ),
  // },
  {
    key: "date",
    title: "Date",
    dataIndex: "date",
    width: 120,
    render: (v: string) => (v ? dayjs(v).format("MM/DD/YYYY") : "-"),
    sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
  },
  {
    key: "type",
    title: "Transaction Type",
    dataIndex: "transactionType",
    width: 140,
    filters: [
      { text: "Invoice", value: "Invoice" },
      { text: "Payment", value: "Payment" },
    ],
    onFilter: (val, rec) => rec.transactionType === val,
  },
  {
    key: "memo",
    title: "Memo/Description",
    dataIndex: "memo",
    ellipsis: true,
  },
  {
    key: "num",
    title: "Num",
    dataIndex: "num",
    width: 120,
  },
  {
    key: "method",
    title: "Payment Method",
    dataIndex: "paymentMethod",
    width: 160,
  },
  {
    key: "tracking",
    title: "Tracking Number",
    dataIndex: "trackingNumber",
    width: 160,
  },
  {
    key: "amount",
    title: "Amount",
    dataIndex: "amount",
    width: 140,
    align: "right" as const,
    render: (v: number) => USDollar.format(v || 0),
    sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
  },
];

// Flatten per-customer invoices[]/details[] into rows for the inner Table
export const flattenCustomerDetails = (record: CustomerGroup): DetailRow[] => {
  const rows: DetailRow[] = [];
  record?.invoices?.forEach((inv) => {
    (inv.details || []).forEach((d, idx) => {
      rows.push({
        key: `${inv.Id}-${idx}`,
        invoiceId: inv.Id,
        invoiceNumber: inv.DocNumber,
        date: d.date,
        transactionType: d.transactionType,
        memo: d.memo,
        num: d.num,
        paymentMethod: d.paymentMethod,
        trackingNumber: d.trackingNumber,
        checkNumber: d.checkNumber,
        amount: d.amount,
        _invoice: inv,
      });
    });
  });
  return rows;
};
